// src/components/TemplateWorkspace/NewWorkspace.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import './templateWorkspace.css'; // Shared CSS

import Sidebar from '../../myDashboard/Sidebar';
import CloseSidebar from '../../myDashboard/CloseSidebar';
import AppTransition from '../../components/appComponents/AppTransition';

import WorkspaceHeader from './workspaceUploads/WorkspaceHeader';
import UploadWorkspace from './workspaceUploads/uploadWorkspace';
import PromptsWorkspace from './promptBox/promptsWorkspace';
import ShortcutsWorkspace from './shortcutsWorkspace';

import { createWorkspace } from './createWorkspace';

import workspace_draft_icon from '../../assets/images/icons/workspace-draft-icon.png';

const NewWorkspace = ({ isSidebarOpen, toggleSidebar }) => {
  const user = auth.currentUser;
  const navigate = useNavigate();

  // ─── METADATA STATE ─────────────────────────────────────────────────────────────
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);

  // ─── MAP STATE FOR PromptsWorkspace ─────────────────────────────────────────────
  const [promptMap,   setPromptMap]   = useState({ "0": "" });
  const [outputMap,   setOutputMap]   = useState({ "0": "" });
  const [chatNameMap, setChatNameMap] = useState({ "0": "New Chat" });
  const [processingMap, setProcessingMap] = useState({ "0": { is_processing: false } });

  // keep the Save button enabled only when there's a title
  useEffect(() => {
    setIsSaveButtonEnabled(workspaceTitle.trim() !== '');
  }, [workspaceTitle]);

  // redirect to list if too many drafts
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const key = `draftCount_${user.uid}`;
    const count = parseInt(localStorage.getItem(key) || '0', 10);
    if (count >= 10) navigate('/workspace');
  }, [user, navigate]);

  // ─── CREATE HANDLER ─────────────────────────────────────────────────────────────
  const handleCreateWorkspace = useCallback(async () => {
    if (!user || !workspaceTitle.trim()) {
      if (!user) navigate('/login');
      return;
    }

    const key = `draftCount_${user.uid}`;
    const count = parseInt(localStorage.getItem(key) || '0', 10);
    if (count >= 10) {
      navigate('/workspace');
      return;
    }

    try {
      const fullId = await createWorkspace({ user, workspaceTitle });
      const id = fullId.replace(/^workspace-/, '');
      navigate(`/workspace/${id}`);
    } catch (e) {
      console.error('Error creating workspace:', e);
    }
  }, [user, workspaceTitle, navigate]);

  return (
    <div className="template-workspace-container">
      {isSidebarOpen
        ? <Sidebar />
        : <CloseSidebar toggleSidebar={toggleSidebar} />}

      <AppTransition>
        <div className="template-workspace-main-new-one">
          <WorkspaceHeader
            workspaceTitle={workspaceTitle}
            setWorkspaceTitle={setWorkspaceTitle}
            workspaceCreatedFunction={handleCreateWorkspace}
            isSaveButtonEnabled={isSaveButtonEnabled}
          />

          <div className="workspace-content-wrapper">
            <div className="template-row-container">
              <div className="template-upload-container">
                <div className="newworkspace-upload-workspace-wrapper">
                  <UploadWorkspace />
                </div>
                <div className="modify-shortcuts-container">
                  <ShortcutsWorkspace />
                </div>
              </div>
            </div>

            <div className="template-prompts-container">
              <PromptsWorkspace
                content_id={null}           // or some placeholder until saved
                isTranscriptAvailable={false}
                isLocalTranscriptAvailable={false}
                timestamps={''}
                summary={''}
                takeaways={[]}
                titles={[]}
                hashtags={''}
                isScRun={false}
                isScTimestampsEnabled={false}
                isScDescriptionEnabled={false}
                isScTakeawaysEnabled={false}
                isScTitlesEnabled={false}
                isScTagsEnabled={false}

                // pass the map state so promptMap["0"] is always defined
                promptMap={promptMap}
                outputMap={outputMap}
                chatNameMap={chatNameMap}
                processingMap={processingMap}
                setPromptMap={setPromptMap}
                setOutputMap={setOutputMap}
                setChatNameMap={setChatNameMap}
                setProcessingMap={setProcessingMap}
              />
            </div>

            <div className="newworkspace-overlay">
              <div className="newworkspace-overlay-message">
                <div className="newworkspace-header-container">
                  <img
                    src={workspace_draft_icon}
                    alt="Draft Icon"
                    className="newworkspace-draft-icon"
                  />
                  <div className="newworkspace-overlay-header">
                    Workspace Not Saved
                  </div>
                </div>
                <p className="newworkspace-overlay-subtitle">
                  Save your workspace to start chatting with videos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AppTransition>
    </div>
  );
};

export default NewWorkspace;
